import { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Estado global del microfono. Permite que el dot pulsante en el HOME
 * refleje una sesion de voz activa que comenzo en el chat, y persiste
 * entre navegaciones (sessionStorage).
 *
 * Eventos:
 *  - 'damyxia:voice-state-change' (window CustomEvent, detail: {active})
 *    se emite cuando cambia para sincronizacion entre vistas.
 *
 * Compatibilidad:
 *  - sessionStorage solo: el estado de voz no debe persistir tras cerrar
 *    el navegador, solo entre pestanas/recargas dentro de la misma sesion.
 */
const VoiceContext = createContext({
  voiceActive: false,
  setVoiceActive: () => {},
});

const STORAGE_KEY = 'damyxia_voice_active';

export const VoiceProvider = ({ children }) => {
  const [voiceActive, setVoiceActiveState] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const setVoiceActive = useCallback((next) => {
    setVoiceActiveState(next);
    try {
      if (next) sessionStorage.setItem(STORAGE_KEY, '1');
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
    try {
      window.dispatchEvent(
        new CustomEvent('damyxia:voice-state-change', { detail: { active: !!next } })
      );
    } catch { /* ignore */ }
  }, []);

  // Solicita a quien tenga el mic activo (ChatInput) que lo detenga.
  // Cualquier vista (HOME, ChatInput) puede invocar esto; ChatInput escucha
  // el evento y llama a stopMicrophone() respetando recursos (stream, recognition).
  const requestStopVoice = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent('damyxia:stop-voice'));
    } catch { /* ignore */ }
    // Fallback inmediato: marcamos inactivo por si el chat no esta montado.
    setVoiceActiveState(false);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  // Escuchar cambios desde otras pestanas/iframes vía storage event
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        setVoiceActiveState(e.newValue === '1');
      }
    };
    const onCustom = (e) => {
      if (typeof e?.detail?.active === 'boolean') {
        setVoiceActiveState(e.detail.active);
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('damyxia:voice-state-change', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('damyxia:voice-state-change', onCustom);
    };
  }, []);

  return (
    <VoiceContext.Provider value={{ voiceActive, setVoiceActive, requestStopVoice }}>
      {children}
    </VoiceContext.Provider>
  );
};

export const useVoice = () => useContext(VoiceContext);

export default VoiceContext;
